import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { useEffect } from 'react';
import { Colors } from '@lean-body/components/styles';
import { SessionStorage } from '@lean-body/src/infra';
import { SwitchButton } from '@lean-body/src/view/atoms/switch_button';
import { MEAL_TASK_REMIND_AT_DEFAULT } from '@lean-body/src/domain';
import { TimeSelectForm, TimeSelectFormat } from '../molecules';
import { WebViewCallbackHandlers, WebViewEvents, } from '@lean-body/src/interfaces';
import { NotificationPermissionAlert } from '../molecules/notification_permission_alert';
const defaultNotificationSettings = {
    news: false,
    live: false,
    mealTaskRemindAt: '',
    streakReminder: false,
};
export const NotificationSettingSubsection = () => {
    const sessionStorage = new SessionStorage();
    const [hasNotificationPermission, setHasNotificationPermission] = React.useState(false);
    const [notificationSettings, setNotificationSettings] = React.useState(defaultNotificationSettings);
    useEffect(() => {
        window.addEventListener(WebViewEvents.deviceNotificationPermission, onDeviceNotificationPermissionEvent);
        // 通知設定をSessionStorageに格納するのはAndroid側で行っているため、
        // WebViewコールバック実行の後、SessionStorageから読み取る
        window.flutter_inappwebview
            .callHandler(WebViewCallbackHandlers.loadNotificationSettings)
            .then(loadNotificationSettings);
        return () => {
            window.removeEventListener(WebViewEvents.deviceNotificationPermission, onDeviceNotificationPermissionEvent);
        };
    }, []);
    const onDeviceNotificationPermissionEvent = (e) => {
        setHasNotificationPermission(e.detail.hasPermission);
    };
    function handleNotificationChange(...args) {
        window.flutter_inappwebview.callHandler(WebViewCallbackHandlers.notificationSettingsChange, ...args).then(() => {
            loadNotificationSettings();
        });
    }
    const loadNotificationSettings = () => {
        const hasPermission = sessionStorage.loadAndroidDeviceNotificationPermission();
        const settings = sessionStorage.loadAndroidNotificationSettings();
        setHasNotificationPermission(hasPermission);
        setNotificationSettings(settings || defaultNotificationSettings);
    };
    const onLiveSwitchChanged = () => {
        handleNotificationChange('live');
    };
    const onNewsSwitchChanged = () => {
        handleNotificationChange('news');
    };
    const onMealTaskReminderSwitchChanged = () => {
        const mealTaskRemindAt = notificationSettings.mealTaskRemindAt ? '' : MEAL_TASK_REMIND_AT_DEFAULT;
        handleNotificationChange('meal_task_reminder', mealTaskRemindAt);
    };
    const onMealTaskRemindTimeChange = (time) => {
        handleNotificationChange('meal_task_reminder', time);
    };
    const onStreakReminderSwitchChanged = () => {
        handleNotificationChange('streak_reminder');
    };
    const onClickNotificationPermissionBanner = () => {
        window.flutter_inappwebview.callHandler(WebViewCallbackHandlers.requestDeviceNotificationPermissionSettings);
    };
    return (React.createElement("div", { className: css(styles.sectionContainer) },
        !hasNotificationPermission && React.createElement(NotificationPermissionAlert, { onClick: onClickNotificationPermissionBanner }),
        React.createElement("div", { className: css(styles.section) },
            React.createElement("div", { className: css(styles.label, !hasNotificationPermission && styles.labelDisabled) }, "LIVE\u914D\u4FE1"),
            React.createElement(SwitchButton, { disabled: !hasNotificationPermission, isOn: hasNotificationPermission && notificationSettings.live, onClick: onLiveSwitchChanged })),
        React.createElement("div", { className: css(styles.section) },
            React.createElement("div", { className: css(styles.label, !hasNotificationPermission && styles.labelDisabled) }, "\u9023\u7D9A\u8A18\u9332\u30EA\u30DE\u30A4\u30F3\u30C0\u30FC"),
            React.createElement(SwitchButton, { disabled: !hasNotificationPermission, isOn: hasNotificationPermission && notificationSettings.streakReminder, onClick: onStreakReminderSwitchChanged })),
        React.createElement("div", { className: css(styles.section) },
            React.createElement("div", { className: css(styles.label, !hasNotificationPermission && styles.labelDisabled) },
                "\u98DF\u4E8B\u8A18\u9332\u306E\u5165\u529B\u5FD8\u308C\u304A\u77E5\u3089\u305B",
                React.createElement("div", { className: css(styles.description, !hasNotificationPermission && styles.descriptionDisabled) }, "1\u5EA6\u3082\u98DF\u4E8B\u5165\u529B\u304C\u306A\u3044\u65E5\u306B\u304A\u77E5\u3089\u305B"),
                hasNotificationPermission && notificationSettings.mealTaskRemindAt && (React.createElement(TimeSelectForm, { selectClassObject: styles.timeSelect, currentValue: notificationSettings.mealTaskRemindAt, setCurrentValue: onMealTaskRemindTimeChange, label: "", format: TimeSelectFormat.hhmm, step: 5, listClassObject: styles.timeSelectList, listItemClassObject: styles.timeSelectListItem, displayIcon: false }))),
            React.createElement(SwitchButton, { disabled: !hasNotificationPermission, isOn: hasNotificationPermission && !!notificationSettings.mealTaskRemindAt, onClick: onMealTaskReminderSwitchChanged })),
        React.createElement("div", { className: css(styles.section) },
            React.createElement("div", { className: css(styles.label, !hasNotificationPermission && styles.labelDisabled) },
                "LEAN BODY\u304B\u3089\u306E\u304A\u77E5\u3089\u305B",
                React.createElement("div", { className: css(styles.description, !hasNotificationPermission && styles.descriptionDisabled) }, "\u30AD\u30E3\u30F3\u30DA\u30FC\u30F3\u3084\u65B0\u7740\u30EC\u30C3\u30B9\u30F3\u60C5\u5831\u3092\u914D\u4FE1")),
            React.createElement(SwitchButton, { disabled: !hasNotificationPermission, isOn: hasNotificationPermission && notificationSettings.news, onClick: onNewsSwitchChanged }))));
};
const styles = StyleSheet.create({
    sectionContainer: {
        margin: '0 30px',
    },
    section: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 0',
        ':first-child': {
            borderTop: `1px solid ${Colors.BorderLightGray}`,
        },
        borderBottom: `1px solid ${Colors.BorderLightGray}`,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.TextDark,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    labelDisabled: {
        color: Colors.Gray,
    },
    description: {
        fontSize: 12,
        fontWeight: 400,
        color: Colors.Gray,
    },
    descriptionDisabled: {
        color: Colors.Gray,
    },
    timeSelect: {
        color: Colors.PrimaryDarkB,
        fontWeight: 500,
        fontSize: 12,
        padding: 0,
        border: 0,
        ':focus': { boxShadow: 'none' },
    },
    timeSelectList: {
        minWidth: 81,
    },
    timeSelectListItem: {
        fontSize: 12,
        minHeight: 33,
        height: 33,
    },
});
